import { FormEvent, useEffect, useState } from 'react';
import { z } from 'zod';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { useAuth } from '@hooks/useAuth';
import { updateUserProfile } from '@lib/firebase/firestore';
import { useFormFields } from '@hooks/useFormFields';
import { validateWithSchema } from '@lib/forms';

const profileSchema = z.object({
  displayName: z.string().min(2, 'Name must be at least 2 characters'),
  phoneNumber: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export function ProfilePage(): JSX.Element {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { values, handleChange, errors: fieldErrors, setErrors, setValues } = useFormFields<ProfileFormData>({
    displayName: user?.displayName || '',
    phoneNumber: user?.phoneNumber || '',
  });

  useEffect(() => {
    if (user) {
      setValues({
        displayName: user.displayName || '',
        phoneNumber: user.phoneNumber || '',
      });
    }
  }, [user, setValues]);

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const validation = validateWithSchema(profileSchema, values);
    if (!validation.success) {
      setErrors(validation.errors);
      return;
    }
    const data = validation.data;

    if (!user) return;

    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await updateUserProfile(user.id, {
        displayName: data.displayName || null,
        phoneNumber: data.phoneNumber || null,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <p className="text-theme-subtext">Please sign in to view your profile.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-4 sm:mb-6">
            My Profile
          </h1>

          <Card>
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-primary mb-4">
                  Personal Information
                </h2>

                <div className="space-y-4">
                  <Input
                    label="Email"
                    type="email"
                    value={user.email}
                    disabled
                    helperText="Email cannot be changed"
                  />

                  <Input
                    label="Full Name"
                    name="displayName"
                    value={values.displayName}
                    onChange={handleChange}
                    error={fieldErrors.displayName}
                  />

                  <Input
                    label="Phone Number"
                    type="tel"
                    name="phoneNumber"
                    value={values.phoneNumber || ''}
                    onChange={handleChange}
                    error={fieldErrors.phoneNumber}
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-theme-error/10 border border-theme-error/30 rounded-lg">
                  <p className="text-sm text-theme-error">{error}</p>
                </div>
              )}

              {saveSuccess && (
                <div className="p-3 bg-theme-success/10 border border-theme-success/30 rounded-lg">
                  <p className="text-sm text-theme-success">
                    Profile updated successfully!
                  </p>
                </div>
              )}

              <Button type="submit" fullWidth isLoading={isSaving}>
                Save Changes
              </Button>
            </form>
          </Card>

          {/* Account Information */}
          <Card className="mt-6">
            <h2 className="text-lg sm:text-xl font-semibold text-primary mb-4">
              Account Information
            </h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-theme-subtext">Account Type:</span>
                <p className="text-primary font-medium capitalize">
                  {user.role}
                </p>
              </div>
              <div>
                <span className="text-sm text-theme-subtext">Member Since:</span>
                <p className="text-primary font-medium">
                  {user.createdAt.toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="text-sm text-theme-subtext">Email Verified:</span>
                <p className="text-primary font-medium">
                  {user.emailVerified ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

